import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Clock, MapPin, Check, X, Upload, Camera,
  QrCode, Ticket, AlertCircle, ChevronRight, Loader2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Reservation Card Component
export const ReservationCard = ({ reservation, onUploadTicket, onCancel }) => {
  const { t } = useTranslation();
  const [showUpload, setShowUpload] = useState(false);
  
  const statusColors = {
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
    expired: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    verification_pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  };
  
  const hoursLeft = Math.max(0, Math.floor((new Date(reservation.expires_at) - new Date()) / (1000 * 60 * 60)));
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900 dark:text-white">{reservation.kupon_title}</h3>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[reservation.status]}`}>
            {t(`reservation.status_${reservation.status}`)}
          </span>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {reservation.kupon_discount}% OFF • {reservation.discounted_price}€
        </p>
      </div>
      
      {/* Details */}
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Calendar className="w-4 h-4" />
          <span>{new Date(reservation.reservation_date).toLocaleDateString()}</span>
        </div>
        
        {reservation.status === 'pending' && hoursLeft > 0 && (
          <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
            <Clock className="w-4 h-4" />
            <span>{t('reservation.expires_in', { hours: hoursLeft })}</span>
          </div>
        )}
        
        {reservation.points_awarded > 0 && (
          <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
            <Check className="w-4 h-4" />
            <span>+{reservation.points_awarded} puntos</span>
          </div>
        )}
        
        {/* Reservation Code */}
        {reservation.status === 'pending' && (
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500 mb-1">Código de reserva</p>
            <p className="font-mono text-lg font-bold text-gray-900 dark:text-white">
              {reservation.id.slice(0, 8).toUpperCase()}
            </p>
          </div>
        )}
      </div>
      
      {/* Actions */}
      {reservation.status === 'pending' && (
        <div className="p-4 pt-0 flex gap-2">
          <Button
            onClick={() => setShowUpload(true)}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            {t('reservation.upload_ticket')}
          </Button>
          <Button
            variant="outline"
            onClick={() => onCancel(reservation.id)}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
      
      {/* Upload Modal */}
      <AnimatePresence>
        {showUpload && (
          <TicketUploadModal
            reservation={reservation}
            onClose={() => setShowUpload(false)}
            onSuccess={(points) => {
              setShowUpload(false);
              onUploadTicket(reservation.id, points);
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Ticket Upload Modal
const TicketUploadModal = ({ reservation, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen no puede superar 10MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  const handleUpload = async () => {
    if (!preview) {
      toast.error('Selecciona una imagen primero');
      return;
    }
    
    setUploading(true);
    
    try {
      const response = await axios.post(
        `${API}/api/reservations/${reservation.id}/ticket`,
        { ticket_image: preview },
        { headers: { 'X-User-Id': user.id } }
      );
      
      if (response.data.verification_status === 'verified') {
        toast.success(t('ticket_upload.success', { points: response.data.points_awarded }));
        onSuccess(response.data.points_awarded);
      } else if (response.data.verification_status === 'pending_manual') {
        toast.info(response.data.message);
        onClose();
      } else {
        toast.error(response.data.message || t('ticket_upload.error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || t('ticket_upload.error'));
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('ticket_upload.title')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
              {t('ticket_upload.instructions')}
            </p>
            <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
              {t('ticket_upload.requirements', { returnObjects: true }).map((req, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Check className="w-3 h-3" />
                  {req}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Upload Area */}
          <div className="relative">
            {preview ? (
              <div className="relative">
                <img 
                  src={preview} 
                  alt="Ticket preview" 
                  className="w-full h-64 object-contain bg-gray-100 dark:bg-gray-900 rounded-lg"
                />
                <button
                  onClick={() => setPreview(null)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-emerald-500 transition-colors">
                <Camera className="w-12 h-12 text-gray-400 mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('ticket_upload.upload_btn')}
                </p>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            onClick={handleUpload}
            disabled={!preview || uploading}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('ticket_upload.processing')}
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Verificar ticket
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Reservation Modal (for creating new reservations)
export const CreateReservationModal = ({ kupon, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // Default to tomorrow
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];
  
  // Max date is kupon expiry
  const maxDate = kupon.expiresAt ? new Date(kupon.expiresAt).toISOString().split('T')[0] : '';
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date) {
      toast.error('Selecciona una fecha');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/api/reservations`,
        {
          kupon_id: kupon.id,
          business_id: kupon.businessId,
          reservation_date: new Date(date).toISOString(),
          notes: notes || null
        },
        { headers: { 'X-User-Id': user.id } }
      );
      
      if (response.data.success) {
        toast.success(t('reservation.success_message'));
        onSuccess(response.data.reservation);
      } else {
        toast.error(response.data.error || 'Error al crear la reserva');
      }
    } catch (error) {
      console.error('Reservation error:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la reserva');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {t('reservation.title')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {kupon.title} - {kupon.discount}% OFF
          </p>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('reservation.date_label')}
            </label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={minDate}
              max={maxDate}
              required
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('reservation.notes_label')}
            </label>
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej: Para 2 personas, cena..."
              className="w-full"
            />
          </div>
          
          {/* Info */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-300">
                <p className="font-medium mb-1">Recuerda:</p>
                <ul className="text-xs space-y-1">
                  <li>• Ve al negocio en la fecha seleccionada</li>
                  <li>• Muestra tu código de reserva</li>
                  <li>• Paga directamente en el local</li>
                  <li>• Sube tu ticket para ganar puntos</li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t('reservation.confirm_btn')
              )}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default ReservationCard;
